import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { GRN } from '@/schemas/grn';

export interface GRNsState {
  byId: Record<string, GRN>;
  allIds: string[];
  filters: {
    purchaseOrderId?: string;
    warehouseId?: string;
    status?: 'accepted' | 'rejected' | 'partial';
    dateFrom?: string;
    dateTo?: string;
  };
  selectedId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: GRNsState = {
  byId: {},
  allIds: [],
  filters: {},
  selectedId: null,
  isLoading: false,
  error: null,
};

const grnsSlice = createSlice({
  name: 'grns',
  initialState,
  reducers: {
    setGRNs: (state, action: PayloadAction<GRN[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((grn) => {
        state.byId[grn.id] = grn;
        state.allIds.push(grn.id);
      });
    },
    addGRN: (state, action: PayloadAction<GRN>) => {
      const grn = action.payload;
      state.byId[grn.id] = grn;
      if (!state.allIds.includes(grn.id)) {
        state.allIds.push(grn.id);
      }
    },
    updateGRN: (state, action: PayloadAction<GRN>) => {
      const grn = action.payload;
      if (state.byId[grn.id]) {
        state.byId[grn.id] = grn;
      }
    },
    removeGRN: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setSelectedGRN: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<GRNsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearGRNs: (state) => {
      state.byId = {};
      state.allIds = [];
    },
  },
});

export const {
  setGRNs,
  addGRN,
  updateGRN,
  removeGRN,
  setSelectedGRN,
  setFilters,
  clearFilters,
  setLoading,
  setError,
  clearGRNs,
} = grnsSlice.actions;

// Selectors
export const selectAllGRNs = (state: { grns: GRNsState }) =>
  state.grns.allIds.map((id) => state.grns.byId[id]);

export const selectGRNById = (state: { grns: GRNsState }, id: string) => state.grns.byId[id];

export const selectFilteredGRNs = (state: { grns: GRNsState }) => {
  const allGRNs = selectAllGRNs(state);
  const { filters } = state.grns;

  return allGRNs.filter((grn) => {
    if (filters.purchaseOrderId && grn.po_id !== filters.purchaseOrderId) {
      return false;
    }
    if (filters.warehouseId && grn.warehouse_id !== filters.warehouseId) {
      return false;
    }
    if (filters.status && grn.quality_status !== filters.status) {
      return false;
    }
    if (filters.dateFrom && grn.received_date < filters.dateFrom) {
      return false;
    }
    if (filters.dateTo && grn.received_date > filters.dateTo) {
      return false;
    }
    return true;
  });
};

export const selectGRNsByPO = (state: { grns: GRNsState }, poId: string) =>
  selectAllGRNs(state).filter((grn) => grn.po_id === poId);

export default grnsSlice.reducer;
